/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik'
import styled from 'styled-components'
import { ArrowSmDownIcon, ArrowSmUpIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { useAppDispatch } from 'store/hooks'
import { serviceAPI, useChangePlanMutation } from 'api/service'
import { ChangePlan, ConfigOption, Value } from 'models/service'
import { extractNumberFromString, isTwoArrayEquals } from 'helpers/utils'
import { setChangePlanTemplate, setChangePlanValue } from 'store/slices/serviceSlice'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    form: RefObject<FormikProps<ChangePlan>>,
    close: () => void,
    isOptionChange: boolean
    setIsOptionChange: (isOptionChange: boolean) => void
}

interface RowProps {
    isOptionChange: boolean
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ChangeServicePlanForm({ form, close, isOptionChange, setIsOptionChange }: Props) {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const id = parseInt(location.pathname?.split('/')[2], 10)
    const { data: service } = serviceAPI.endpoints.service.useQueryState(id)
    const { data, isSuccess: availablePlanSuccess } = serviceAPI.endpoints.availablePlans.useQueryState(id)
    const [changePlan, { isSuccess, isError }] = useChangePlanMutation()
    const configOption = useMemo(() => {
        if (data && data.config_options.length >= 1) {
            return data.config_options.map(({ id, name, values }: ConfigOption) => {
                return {
                    name,
                    config_id: id,
                    option_id: values[0].id
                }
            })
        }

        return []
    }, [data])
    const options = useMemo(() => {
        if (service && service.config) {
            return service.config.map(({ option_id }) => option_id)
        }

        return []
    }, [service])

    function renderChanges(initialOptionId: number = 0, changedOptionId: number = 0, configOptions: Value[], isOptionChange: boolean) {
        const initialOption = configOptions.find(({ id }) => id === initialOptionId)
        const changeOption = configOptions.find(({ id }) => id === changedOptionId)

        if (!isOptionChange) {
            return null
        }

        if (initialOptionId === changedOptionId) {
            return '-'
        }

        return (
            <ChangeWrapper>
                {extractNumberFromString(initialOption?.name) < extractNumberFromString(changeOption?.name)
                    ? <ArrowSmUpIcon className={'up'}/>
                    : <ArrowSmDownIcon className={'down'}/>
                }
                <div>{initialOption?.name}</div>
            </ChangeWrapper>
        )
    }

    function PlanPaths() {
        if (data && data.plan_paths.length >= 1) {
            return (
                <PlanPathsWrapper>
                    <Field
                        name={'product_id'}
                        label={'Available Products'}
                        type={'select'}
                        list={data.plan_paths.map(({ id, name, description }) => ({
                            label: `${name} - ${description}`,
                            value: id
                        }))}
                        component={NXUIForm.SelectField}
                    />
                </PlanPathsWrapper>
            )
        }

        return null
    }

    function DetermineChanges() {
        const { values } = useFormikContext()

        useEffect(() => {
            if (form.current) {
                const { initialValues, values } = form.current

                if (isTwoArrayEquals<number[]>(initialValues.options, values.options.map((option) => +option))) {
                    setIsOptionChange(false)
                } else {
                    setIsOptionChange(true)
                }
            }
        }, [values])

        return null
    }

    function ConfigOption() {
        if (configOption && configOption.length >= 1) {
            return (
                <>
                    {configOption.map((config, index) => {
                        const configOption: any = data?.config_options.find((configOption: ConfigOption) => {
                            return configOption.id === config.config_id
                        })

                        if (configOption) {
                            const values = configOption.values.map(({ id, name }: Value) => {
                                return {
                                    label: name,
                                    value: id
                                }
                            })
                            const changedOption = form.current?.values.options[index]
                                ? parseInt(form.current.values.options[index].toString(), 10)
                                : form.current?.values.options[index]

                            return (
                                <Row key={configOption.id} isOptionChange={isOptionChange}>
                                    <Field
                                        name={`options[${index}]`}
                                        label={configOption.name}
                                        type={'select'}
                                        list={values}
                                        component={NXUIForm.SelectField}
                                    />
                                    {renderChanges(form.current?.initialValues.options[index], changedOption, configOption.values, isOptionChange)}
                                    <DetermineChanges/>
                                </Row>
                            )
                        }

                        return null
                    })}
                </>
            )
        }

        return null
    }

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if ((isSuccess || isError) && form.current) {
            form.current.setSubmitting(false)
            form.current.resetForm()
        }

        if (isSuccess) {
            close()
        }
    }, [form, isSuccess, isError])

    useEffect(() => {
        if (availablePlanSuccess && form.current && data && data.plan_paths.length >= 1) {
            form.current.setFieldValue('product_id', data.plan_paths[0].id)
        }
    }, [availablePlanSuccess, data])

    return (
        <Formik
            innerRef={form}
            initialValues={{
                preview: true,
                billing_cycle_id: service ? service.billing_cycle.id : null,
                product_id: service ? service.product.id : null,
                options: options
            }}
            onSubmit={({ preview, billing_cycle_id, product_id, options }) => {
                const config = configOption.map(({ config_id }, index) => {
                    return {
                        config_id,
                        option_id: options[index]
                    }
                })
                changePlan({
                    id,
                    preview,
                    billing_cycle_id,
                    product_id,
                    config
                })
                dispatch(setChangePlanValue({ id, preview, billing_cycle_id, product_id, options, config }))
                dispatch(setChangePlanTemplate('confirm'))
            }}
        >
            {() => (
                <Form id={'changePlanForm'}>
                    <ConfigOption/>
                    <PlanPaths/>
                </Form>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Row = styled.div<RowProps>`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;

    > div:first-child {
        flex: ${props => props.isOptionChange ? '0 0 80%' : '0 0 100%'}
    }
`

const ChangeWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;

    svg {
        width: 20px;
        height: 20px;

        &.up {
            color: ${props => props.theme['confirm--100']}
        }

        &.down {
            color: ${props => props.theme['error--100']}
        }
    }
`

const PlanPathsWrapper = styled.div`
    select {
        white-space: pre-line;
    }
`
